import { useCallback, useMemo, useState } from 'react';
import { AssetHydration } from '@frameio/assets/hydration';
import {
  DrillDown,
  DrillDownScrollingColumn,
  LoadingContainer,
} from '@frameio/vapor';
import { WorkspaceProjectList } from '@frameio/workspaces/shared/components/WorkspaceProjectList';
import type { WorkspaceProjectSelectCallbackOptions } from '@frameio/workspaces/shared/components/WorkspaceProjectList/types';
import { FoldersView } from './FoldersView';
import TEXT from './generated/TEXT/ExportLocation';
import {
  useOnResourceSelect,
  usePaginatedWorkspaceListData,
  useWorkspaceProject,
} from './hooks';

TEXT.DEFINE({
  /** Label displayed while content is loading */
  loadingLabel: 'Loading content...',
  /** Label for the back button */
  backLabel: 'Go back',
});

/**
 * Enum representing the two views in the export location drill-down interface.
 * Used to track which column is currently active.
 */
enum View {
  /** The workspace/project list view (left column) */
  PROJECTS_VIEW = 0,
  /** The folder tree view (right column) */
  FOLDERS_VIEW,
}

/**
 * Component that provides a drill-down interface for selecting export
 * locations. Displays a two-column view: workspace/project list on the left and
 * folder tree on the right. Validates selected resources (projects and folders)
 * exist before allowing selection.
 *
 * @param props - Component props
 * @param props.projectId - Currently selected project ID, or null if none
 * selected
 * @param props.setProjectId - Callback to update the selected project ID
 * @param props.folderId - Currently selected folder ID, or null if none
 * selected
 * @param props.setFolderId - Callback to update the selected folder ID
 * @param props.startInFoldersView - If true, starts in the folders view instead
 * of projects view (default: false)
 */
export const ExportLocation = ({
  startInFoldersView = false,
  projectId,
  setProjectId,
  folderId,
  setFolderId,
}: {
  projectId: string | null;
  setProjectId: (projectId: string | null) => void;
  startInFoldersView?: boolean;
  folderId: string | null;
  setFolderId: (folderId: string | null) => void;
}) => {
  const [currentView, setCurrentView] = useState<View>(
    startInFoldersView ? View.FOLDERS_VIEW : View.PROJECTS_VIEW
  );

  const {
    project,
    workspace,
    onWorkspaceProjectSelect: onWorkspaceProjectSelectCallback,
  } = useWorkspaceProject(projectId);

  const { onResourceSelect } = useOnResourceSelect();

  const onWorkspaceProjectSelectSuccess = useCallback(
    (data: WorkspaceProjectSelectCallbackOptions) => {
      onWorkspaceProjectSelectCallback(data);
      setProjectId(data.project.id);
      setFolderId(data.project.rootAssetId);
      setCurrentView(View.FOLDERS_VIEW);
    },
    [
      setCurrentView,
      setFolderId,
      setProjectId,
      onWorkspaceProjectSelectCallback,
    ]
  );

  const onFolderSelectSuccess = useCallback(
    (data: { folderId: string }) => {
      setFolderId(data.folderId);
    },
    [setFolderId]
  );

  const { workspacesData, loading, handleFetchNextOnScroll } =
    usePaginatedWorkspaceListData();

  const projects = useMemo(
    () =>
      workspacesData ? (
        <DrillDownScrollingColumn enableTopBorder={false}>
          <WorkspaceProjectList
            data={workspacesData}
            navOnClick={false}
            onScroll={handleFetchNextOnScroll}
            onWorkspaceProjectSelect={(data) => {
              onResourceSelect({
                data,
                onSuccess: onWorkspaceProjectSelectSuccess,
              });
            }}
          />
        </DrillDownScrollingColumn>
      ) : null,
    [
      handleFetchNextOnScroll,
      workspacesData,
      onResourceSelect,
      onWorkspaceProjectSelectSuccess,
    ]
  );

  const folders = useMemo(
    () => (
      <FoldersView
        disableBack={false}
        onBackSelect={() => {
          setProjectId(null);
          setCurrentView(View.PROJECTS_VIEW);
          setFolderId(null);
        }}
        onFolderSelect={(id) =>
          onResourceSelect({
            data: { folderId: id ?? '' },
            onSuccess: onFolderSelectSuccess,
          })
        }
        project={project ?? undefined}
        targetFolderId={folderId ?? undefined}
        workspace={workspace ?? undefined}
      />
    ),
    [
      setFolderId,
      project,
      folderId,
      workspace,
      setProjectId,
      onResourceSelect,
      onFolderSelectSuccess,
    ]
  );

  return (
    <AssetHydration>
      <LoadingContainer isLoading={loading} loadingLabel={TEXT.loadingLabel}>
        <DrillDown
          backLabel={TEXT.backLabel}
          columnBackgroundColor="primary"
          columns={[projects, folders].map((content) => ({ content }))}
          disableColumnsTitle={true}
          onChange={(i) => setCurrentView(i)}
          value={currentView}
        />
      </LoadingContainer>
    </AssetHydration>
  );
};
